import * as Sentry from "@sentry/browser";
//#region ../src/utils/isolated-registry.ts
function createIsolatedRegistry(options) {
	const { onError } = options;
	const callbacks = /* @__PURE__ */ new Set();
	/**
	* Cached iteration snapshot, invalidated on every mutation.
	*
	* Caching does not change the SEMANTICS — a register/unregister during a run
	* still defers to the next run — it only avoids re-allocating an identical
	* array at 60–90 Hz between registry changes, which are rare (PR #67 review).
	*/
	let snapshot = null;
	let snapshotCount = 0;
	const takeSnapshot = () => {
		if (snapshot === null) {
			snapshot = Array.from(callbacks);
			snapshotCount++;
		}
		return snapshot;
	};
	const invoke = (fns, args) => {
		for (const fn of fns) try {
			fn(...args);
		} catch (error) {
			try {
				onError(error);
			} catch {}
		}
	};
	return {
		register(fn) {
			callbacks.add(fn);
			snapshot = null;
			return () => {
				callbacks.delete(fn);
				snapshot = null;
			};
		},
		run(...args) {
			invoke(takeSnapshot(), args);
		},
		runOnce(...args) {
			const fns = takeSnapshot();
			callbacks.clear();
			snapshot = null;
			invoke(fns, args);
		},
		clear() {
			callbacks.clear();
			snapshot = null;
		},
		get size() {
			return callbacks.size;
		},
		get snapshotCount() {
			return snapshotCount;
		}
	};
}
//#endregion
//#region ../src/utils/logger.ts
/**
* Configurable Logger Utility
*
* Provides a simple logging API with configurable log levels.
* This allows disabling verbose logging in production while keeping
* it available for development and debugging.
*
* Also maintains an in-memory ring buffer of recent log entries
* for display in the expandable log panel UI.
*
* Sentry Integration:
* - All log levels add Sentry breadcrumbs for debugging context
* - log.warn() reports a standalone Sentry Issue (captureMessage)
* - log.error() reports a standalone Sentry Issue: captureException for Error
*   arguments, or a captureMessage fallback for string-only errors
* - warn/error Issues are grouped by a normalized message template
*   (['log', level, tag, template]) so dynamic values (frame indices, sizes,
*   filenames) collapse into one Issue per message kind without merging
*   genuinely different messages that share a tag
*/
/**
* Log levels in order of verbosity (lower = more verbose)
*/
let LogLevel = /* @__PURE__ */ function(LogLevel) {
	LogLevel[LogLevel["DEBUG"] = 0] = "DEBUG";
	LogLevel[LogLevel["INFO"] = 1] = "INFO";
	LogLevel[LogLevel["WARN"] = 2] = "WARN";
	LogLevel[LogLevel["ERROR"] = 3] = "ERROR";
	LogLevel[LogLevel["SILENT"] = 4] = "SILENT";
	return LogLevel;
}({});
/**
* Global log level - controls what gets logged across all loggers
*/
let globalLogLevel = 0;
/**
* Maximum number of log entries to keep in the ring buffer
*/
const LOG_BUFFER_MAX_SIZE = 100;
/**
* In-memory ring buffer of recent log entries
*/
let logBuffer = [];
/**
* WHY THE ERROR SINK IS `console.error` AND NOT THIS MODULE'S OWN LOGGING.
* Reporting a throwing subscriber through the logger would append an entry,
* which notifies the subscribers, which throws again — an infinite recursion.
* That hazard is the reason `createIsolatedRegistry` takes the sink as an
* option instead of importing a logger itself (which would also make
* `logger → isolated-registry → logger` a cycle the `check:cycles` gate
* rejects).
*
* ADOPTING THE REGISTRY ALSO FIXED AN INCONSISTENCY. The old array snapshotted
* unsubscribes by accident — `subscribers.filter(...)` REASSIGNS, so a
* `for...of` already in flight kept the old array — while `push` mutated in
* place, so a subscriber added during a dispatch received the very entry it
* had not been subscribed for. Now both defer to the next entry, which is what
* every other registry in the framework already did.
*/
const subscribers = createIsolatedRegistry({ onError: (err) => console.error("[Logger] Subscriber threw an error:", err) });
/**
* Serialize an Error instance to an object capturing name, message, stack,
* and any enumerable properties.
*/
function serializeError(error) {
	const result = {
		name: error.name,
		message: error.message,
		stack: error.stack
	};
	for (const key of Object.keys(error)) result[key] = error[key];
	return result;
}
/**
* Safely stringify an object, handling circular references.
* Uses a WeakSet to track seen objects and replaces cycles with a placeholder.
*/
function safeStringify(value) {
	const seen = /* @__PURE__ */ new WeakSet();
	const replacer = (_key, val) => {
		if (typeof val === "bigint") return val.toString();
		if (val !== null && typeof val === "object") {
			if (seen.has(val)) return "[Circular]";
			seen.add(val);
		}
		return val;
	};
	try {
		return JSON.stringify(value, replacer);
	} catch {
		return "[Unserializable]";
	}
}
/**
* Serialize a single argument to a string for the log buffer.
* Special-cases Error instances and handles circular/complex objects safely.
*/
function serializeArg(arg) {
	if (arg === null) return "null";
	if (arg === void 0) return "undefined";
	if (typeof arg === "string") return arg;
	if (typeof arg === "number" || typeof arg === "boolean") return String(arg);
	if (typeof arg === "bigint") return arg.toString();
	if (typeof arg === "symbol") return arg.toString();
	if (typeof arg === "function") return arg.name ? `[Function: ${arg.name}]` : "[Function]";
	if (arg instanceof Error) return safeStringify(serializeError(arg));
	return safeStringify(arg);
}
/**
* Serialize a log call's arguments once (quality-review C-5 — the buffer,
* breadcrumb, and Sentry-message paths each used to re-serialize the same
* args, i.e. 2–3× per log call). All downstream consumers take the string.
*/
function serializeArgs(args) {
	return args.map((arg) => serializeArg(arg)).join(" ");
}
/**
* Add an entry to the log buffer and notify subscribers
*/
function addToBuffer(level, tag, message) {
	const entry = {
		timestamp: Date.now(),
		level,
		tag,
		message
	};
	logBuffer.push(entry);
	if (logBuffer.length > LOG_BUFFER_MAX_SIZE) logBuffer.shift();
	subscribers.run(entry);
}
/**
* Get a copy of the current log buffer
* @returns Array of log entries (copy to prevent external mutation)
*/
function getLogBuffer() {
	return [...logBuffer];
}
/**
* Clear all entries from the log buffer
*/
function clearLogBuffer() {
	logBuffer = [];
}
/**
* Subscribe to new log entries
* @param callback - Function called when a new log entry is added
* @returns Unsubscribe function
*/
function subscribeToLogs(callback) {
	return subscribers.register(callback);
}
/**
* Set the global log level
* @param level - The minimum level to log
*/
function setGlobalLogLevel(level) {
	globalLogLevel = level;
}
/**
* Get the current global log level
*/
function getGlobalLogLevel() {
	return globalLogLevel;
}
/**
* Map LogLevel to Sentry breadcrumb severity.
* Sentry uses 'warning' (not 'warn') for the warning level.
*/
function toSentryLevel(level) {
	switch (level) {
		case 0: return "debug";
		case 1: return "info";
		case 2: return "warning";
		case 3: return "error";
		default: return "info";
	}
}
/**
* Add a Sentry breadcrumb for debugging context.
* Breadcrumbs are attached to future exceptions, helping trace what happened before an error.
*/
function addSentryBreadcrumb(level, tag, body) {
	const message = `[${tag}] ${body}`;
	Sentry.addBreadcrumb({
		category: "log",
		level: toSentryLevel(level),
		message
	});
}
/**
* Normalize a log message body into a stable "template" for Sentry grouping.
*
* Replaces the dynamic tokens that typically vary between otherwise-identical
* log lines (numbers, UUIDs, quoted strings) with placeholders. This makes the
* fingerprint depend on the *kind* of message rather than its concrete values,
* so that:
* - the same message with different dynamic values collapses into one Issue
*   (e.g. `frame 12` / `frame 87` -> `frame {n}`), and
* - two genuinely different messages under the same tag stay as separate
*   Issues (because their templates differ).
*
* Order matters: UUIDs are replaced before bare numbers, otherwise the number
* rule would shred the digit groups inside a UUID first.
*/
function toFingerprintTemplate(body) {
	return body.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "{uuid}").replace(/-?\d+(?:\.\d+)?(?:e[+-]?\d+)?/gi, "{n}").replace(/"[^"]*"/g, "\"{str}\"").replace(/'[^']*'/g, "'{str}'");
}
/**
* Capture a log line as a standalone Sentry Issue (message event).
*
* Shared by `reportWarningToSentry` and the string-only branch of
* `reportErrorsToSentry`. The fingerprint is
* `['log', level, tag, <normalized template>]` so that dynamic values in the
* message do not fragment one logical problem into many Issues, while distinct
* messages from the same tag remain distinct Issues (see
* {@link toFingerprintTemplate}).
*/
function captureLogMessage(level, tag, body) {
	const message = `[${tag}] ${body}`;
	Sentry.captureMessage(message, {
		level,
		fingerprint: [
			"log",
			level,
			tag,
			toFingerprintTemplate(body)
		]
	});
}
/**
* Report a warning to Sentry as a standalone message.
* Called for log.warn() so warnings are independently visible in the
* Sentry dashboard, not only as breadcrumbs attached to later exceptions.
*/
function reportWarningToSentry(tag, body) {
	captureLogMessage("warning", tag, body);
}
/**
* Report log.error() arguments to Sentry as standalone Issues.
*
* - For every argument that is an `Error`, calls `captureException` so the
*   Issue carries a full stack trace.
* - If NO argument is an `Error` (a string-only `log.error(...)`), falls back
*   to a message event so the error still surfaces as an Issue instead of only
*   a breadcrumb/Log.
*
* The fallback is mutually exclusive with `captureException` to avoid duplicate
* Issues when an Error is present, and groups via the same normalized template
* fingerprint as warnings (see {@link captureLogMessage}).
*/
function reportErrorsToSentry(tag, args, body) {
	let capturedError = false;
	for (const arg of args) if (arg instanceof Error) {
		Sentry.captureException(arg);
		capturedError = true;
	}
	if (!capturedError) captureLogMessage("error", tag, body);
}
/**
* Create a logger with a specific tag prefix
* @param tag - The tag to prefix all log messages with (e.g., 'GPS', 'Storage')
* @returns A logger object with debug, info, warn, error methods
*
* @example
* ```typescript
* const log = createLogger('GPS');
* log.info('Watch started'); // [GPS] Watch started
* log.error('Error:', err);  // [GPS] Error: <error details>
* ```
*/
function createLogger(tag) {
	const prefix = `[${tag}]`;
	return {
		debug: (...args) => {
			const body = serializeArgs(args);
			addToBuffer(0, tag, body);
			addSentryBreadcrumb(0, tag, body);
			if (globalLogLevel <= 0) console.log(prefix, ...args);
		},
		info: (...args) => {
			const body = serializeArgs(args);
			addToBuffer(1, tag, body);
			addSentryBreadcrumb(1, tag, body);
			if (globalLogLevel <= 1) console.log(prefix, ...args);
		},
		warn: (...args) => {
			const body = serializeArgs(args);
			addToBuffer(2, tag, body);
			addSentryBreadcrumb(2, tag, body);
			reportWarningToSentry(tag, body);
			if (globalLogLevel <= 2) console.warn(prefix, ...args);
		},
		error: (...args) => {
			const body = serializeArgs(args);
			addToBuffer(3, tag, body);
			addSentryBreadcrumb(3, tag, body);
			reportErrorsToSentry(tag, args, body);
			if (globalLogLevel <= 3) console.error(prefix, ...args);
		}
	};
}
//#endregion
export { getLogBuffer as a, createIsolatedRegistry as c, getGlobalLogLevel as i, clearLogBuffer as n, setGlobalLogLevel as o, createLogger as r, subscribeToLogs as s, LogLevel as t };
